import { cn } from "@gettactic/helpers";
import { Check, Maximize2 } from "lucide-react";
import React from "react";
import { ImageThumbnail } from "../../data-display/ImageThumbnail";
import { ImageOption } from "./ImageOptionPicker.types";

export interface ImageOptionRowProps {
  option: ImageOption;
  isSelected: boolean;
  onToggle: () => void;
  /** Omitted when the option has no image to preview. */
  onPreview?: () => void;
}

/**
 * One option in the picker's dropdown: a large image, the label, and a button
 * to preview the image full size.
 *
 * The row's own click target is an overlay button covering the row rather than
 * a button wrapping the content, so the preview button can sit above the image
 * without nesting one button inside another.
 */
export const ImageOptionRow: React.FC<ImageOptionRowProps> = ({
  option,
  isSelected,
  onToggle,
  onPreview
}) => (
  <li
    className={cn(
      "relative flex items-center gap-3 rounded-xl p-2 transition-colors",
      isSelected ? "bg-neutral-100" : "hover:bg-neutral-50"
    )}
  >
    <span className="relative">
      <ImageThumbnail src={option.imageUrl} size="lg" />
      {onPreview ? (
        <button
          type="button"
          aria-label={`Preview the image for ${option.label}`}
          onClick={onPreview}
          className={cn(
            "absolute top-1 right-1 z-10 flex h-6 w-6 items-center justify-center rounded-md",
            "bg-white/90 text-neutral-700 shadow-sm transition-colors hover:bg-white hover:text-neutral-900",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          )}
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </span>

    <span className="min-w-0 flex-1">
      <span className="block truncate font-medium text-neutral-900 text-sm">
        {option.label}
      </span>
      {option.description ? (
        <span className="mt-0.5 block truncate text-content text-sm">
          {option.description}
        </span>
      ) : null}
    </span>

    {isSelected ? (
      <Check className="mr-1 h-4 w-4 shrink-0 text-neutral-900" />
    ) : null}

    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onToggle}
      className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <span className="sr-only">{option.label}</span>
    </button>
  </li>
);
